import { createContext, useContext, useState } from "react";
import axios from "axios";

import { IAddressProps } from "interfaces/Address";
import { accountData, IBankingDataProps } from "interfaces/BankingData";
import { IContactProps } from "interfaces/Contact";
import { IUser, IUserToPolicemanProps } from "interfaces/User";
import { IVehicleProps } from "interfaces/Vehicle";

import { getPolicemanService } from "services/PolicemenServices";

import {
  AddressInfo,
  ContextProviderProps,
  FormError,
  PolicemanContextData,
  IPolicemanRegister,
  PreviewObject,
  FilesObject,
} from "./IPolicemanContext";
import { IProvider } from "interfaces/Provider";

export const INITIAL_PREVIEW_OBJECT = {
  documentFront: undefined,
  documentBack: undefined,
  selfie: undefined,
  selfieWithDoc: undefined,
  vehicle: undefined,
};

export const INITIAL_FILES_OBJECT = {
  documentFront: undefined,
  documentBack: undefined,
  selfie: undefined,
  selfieWithDoc: undefined,
  vehicle: undefined,
};

export const PolicemanContext = createContext({} as PolicemanContextData);

export function PolicemanRegisterProvider({
  children,
}: ContextProviderProps): JSX.Element {
  const [user, setUser] = useState<IUserToPolicemanProps | undefined>();
  const [policeman, setPoliceman] = useState<IPolicemanRegister>({
    publicAgent: false,
    working: false,
    user: {} as IUser,
  } as IPolicemanRegister);
  const [selectedProvider, setSelectedProvider] = useState<IProvider>(
    {} as IProvider
  );
  const [vehicle, setVehicle] = useState<IVehicleProps>({} as IVehicleProps);
  const [contact, setContact] = useState<IContactProps>({} as IContactProps);
  const [address, setAddress] = useState<IAddressProps>({
    city: "",
  } as IAddressProps);
  const [bankingData, setBankingData] = useState<IBankingDataProps>({
    accountData: {} as accountData,
  } as IBankingDataProps);

  const [errors, setErrors] = useState<FormError>({});
  const [searching, setSearching] = useState<boolean>(false);

  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const [isClientAssociationRegister, setIsClientAssociationRegister] =
    useState<boolean>(false);

  const [searchingPostalCode, setSearchingPostalCode] =
    useState<boolean>(false);

  // states to deal with the upload of profile pics
  const [preview, setPreview] = useState<PreviewObject>(INITIAL_PREVIEW_OBJECT);

  // states to deal with the upload of profile pics
  const [files, setFiles] = useState<FilesObject>(INITIAL_FILES_OBJECT);

  async function retrievePoliceman(id: string) {
    setSearching(true);
    await getPolicemanService(id)
      .then((res) => {
        console.log("----->", res.item);

        const _policeman = {
          publicAgent: res.item.policeman.publicAgent,
          region: res.item.policeman.region,
          working: res.item.policeman.working,
          id: res.item.policeman.id,
          active: res.item.policeman.active,
          approved: res.item.policeman.approved,
          createdAt: res.item.policeman.createdAt,
          occurrencesAttended: res.item.policeman.occurrencesAttended,
          occurrencesDenied: res.item.policeman.occurrencesDenied,
          updatedAt: res.item.policeman.updatedAt,
          patrimonialValues: res.item.policeman.patrimonialValues,
          vehicleValues: res.item.policeman.vehicleValues,
          pictures: res.item.policeman.pictures,
          user: res.item,
        };

        setBankingData({ ...res.item.bankAccount });
        setContact(res.item.contact || ({} as IContactProps));
        setPoliceman(_policeman);
        setVehicle(res.item.vehicle);
        setUser(res.item);
        setAddress(res.item.address);
        setSearching(false);
      })
      .catch((error) => {
        console.log(error.response.item);
        setSearching(false);
      });
  }

  function resetPoliceman() {
    setSearching(true);

    setPoliceman(() => {
      return {
        publicAgent: false,
        working: false,
        user: {} as IUser,
        patrimonialValues: {},
        vehicleValues: {},
      } as IPolicemanRegister;
    });
    setVehicle({} as IVehicleProps);
    setContact({} as IContactProps);
    setAddress({} as IAddressProps);
    setBankingData({
      accountData: {} as accountData,
    } as IBankingDataProps);
    setFiles(INITIAL_FILES_OBJECT);
    setPreview(INITIAL_PREVIEW_OBJECT);

    setSearching(false);
  }

  async function addressByPostalCode(postalCode: string) {
    const postalCodeFormatted = postalCode.replace("_", "");
    if (postalCodeFormatted.trim().length !== 9) return;

    setSearchingPostalCode(true);

    const response = await axios.get(
      `https://viacep.com.br/ws/${postalCode}/json/`
    );
    if (response.status === 200) {
      const addressInfo: AddressInfo = response.data;

      const _addressRegister = { ...address };
      _addressRegister.city = addressInfo.localidade;
      _addressRegister.street = addressInfo.logradouro;
      _addressRegister.uf = addressInfo.uf;
      _addressRegister.district = addressInfo.bairro;

      setAddress(_addressRegister);
      setSearchingPostalCode(false);
    } else {
      setSearchingPostalCode(false);
      setErrors((value) => {
        return { ...value, postalCode: "CEP inválido inserido" };
      });
    }
  }

  function addError(field: string, message: string) {
    const _errors = { ...errors };
    _errors[field] = message;

    setErrors(() => {
      return _errors;
    });
  }

  function removeError(field: string) {
    console.log(
      "🚀 ~ file: PolicemanContext.tsx:150 ~ removeError ~ field:",
      field
    );
    const _errors = { ...errors };
    delete _errors[field];

    setErrors(() => {
      return _errors;
    });
  }

  return (
    <PolicemanContext.Provider
      value={{
        user,
        setUser,
        policeman,
        setPoliceman,
        vehicle,
        setVehicle,
        contact,
        setContact,
        bankingData,
        setBankingData,
        address,
        setAddress,
        addressByPostalCode,
        errors,
        setErrors,
        addError,
        removeError,
        retrievePoliceman,
        resetPoliceman,
        searching,
        searchingPostalCode,
        setSearchingPostalCode,
        hasChanges,
        setHasChanges,
        selectedProvider,
        setSelectedProvider,
        isClientAssociationRegister,
        setIsClientAssociationRegister,
        preview,
        setPreview,
        files,
        setFiles,
      }}
    >
      {children}
    </PolicemanContext.Provider>
  );
}

export const usePolicemanRegister = () => {
  return useContext(PolicemanContext);
};
